
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import { sharedState } from '../../../../framework/state';
import { getResource, postResource } from '../../../../framework/client/resource';
import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import { VendorContractType } from '../../../../../common/application/model/enums/VendorContractType';
import { VendorContract } from '../../../../../common/application/model/VendorContract';
import { getEnumerationOptions, OptionValue } from '../../../../framework/service/options';
import SwitchField from '../../../../framework/fields/SwitchField.vue';
import {
    getAccountOptions,
    getCustomerOptions,
    getVehicleContractOptions,
    getVendorOptions,
} from '../../../service/application_options_service';
import NumberField from '../../../../framework/fields/NumberField.vue';
import { VendorContractScope } from '../../../../../common/application/model/enums/VendorContractScope';
import DateField from '../../../../framework/fields/DateField.vue';
import { PaymentType } from '../../../../../common/application/model/enums/PaymentType';
import { BudgetType } from '../../../../../common/application/model/enums/BudgetType';
import { AccountType } from '../../../../../common/application/model/enums/AccountType';
import { Vendor } from '../../../../../common/application/model/Vendor';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { VehicleContract } from '../../../../../common/application/model/VehicleContract';
import CurrencyAmountField from '../../../../framework/fields/CurrencyAmountField.vue';

@Component({
    components: {
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        ValidationObserver,
    },
})
export default class AddOrderVendorContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR_CONTRACT;

    $refs!: {
        observer: Vue;
    };

    // Properties
    @Prop(String) readonly name!: string;
    @Prop(String) readonly vendorId!: string;
    @Prop(String) readonly customerId!: string;
    @Prop(String) readonly vehicleContractId!: string;
    @Prop(String) readonly vendorContractType!: VendorContractType;
    @Prop(String) readonly vendorContractScope!: VendorContractScope;
    @Prop(String) readonly vehicleRegistrationNumber!: string;
    @Prop(String) readonly paymentType!: PaymentType;
    @Prop(String) readonly budgetType!: BudgetType;
    @Prop(String) readonly startDate!: string;
    @Prop(String) readonly endDate!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as VendorContract,
        customerOptions: [] as OptionValue[],
        vendorOptions: [] as OptionValue[],
        vehicleContractOptions: [] as OptionValue[],
        vendorContractTypeOptions: [] as OptionValue[],
        vendorContractScopeOptions: [] as OptionValue[],
        paymentTypeOptions: [] as OptionValue[],
        budgetTypeOptions: [] as OptionValue[],
        customerExpenseAccountOptions: [] as OptionValue[],
        showAdditionalInformation: false,
    };

    // Functions
    async mounted() {
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorOptions = await getVendorOptions();
        // this.local.vehicleContractOptions = await getVehicleContractOptions([this.local.row.customerId]);
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        this.local.vendorContractScopeOptions = getEnumerationOptions(
            'VendorContractScope',
            Object.keys(VendorContractScope),
            this,
        );
        this.local.paymentTypeOptions = getEnumerationOptions('PaymentType', Object.keys(PaymentType), this);
        this.local.budgetTypeOptions = getEnumerationOptions('BudgetType', Object.keys(BudgetType), this);

        this.local.row = {
            ...this.local.row,
            vendorContractType: this.vendorContractType,
            vendorContractScope: this.vendorContractScope,
            paymentType: this.paymentType,
            budgetType: this.budgetType,
        };

        await this.customerPropertyIdChange(this.customerId);
        await this.startDatePropertyIdChange(this.startDate);
        await this.endDatePropertyIdChange(this.endDate);
    }

    @Watch('vehicleContractId')
    async vehicleContractIdPropertyIdChange(selectedVehicleContractId: string | undefined) {
        this.local.row = {
            ...this.local.row,
            vehicleContractId: selectedVehicleContractId!!,
        };
    }

    @Watch('vendorId')
    async vendorIdPropertyChange(vendorId: string | undefined) {
        this.local.row = {
            ...this.local.row,
            vendorId: vendorId!!,
        };
    }

    @Watch('customerId')
    async customerPropertyIdChange(selectedCustomerId: string | undefined) {
        if (!selectedCustomerId) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            customerId: selectedCustomerId!!,
        };
        this.local.vehicleContractOptions = await getVehicleContractOptions([this.local.row.customerId]);
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('vehicleRegistrationNumber')
    async vehicleRegistrationNumberChange(vehicleRegistrationNumber: string | undefined) {
        this.local.row = {
            ...this.local.row,
            vehicleRegistrationNumber: vehicleRegistrationNumber as any,
        };
    }

    @Watch('startDate')
    async startDatePropertyIdChange(newStartDate: string | undefined) {
        this.local.row = {
            ...this.local.row,
            startDate: newStartDate as any,
        };
    }

    @Watch('endDate')
    async endDatePropertyIdChange(newEndDate: string | undefined) {
        this.local.row = {
            ...this.local.row,
            endDate: newEndDate as any,
        };
    }

    @Watch('local.row.vendorId')
    async vendorIdChange(vendorId: string | undefined) {
        if (vendorId) {
            const vendor = await getResource<Vendor>(ApplicationResource.VENDOR, vendorId);
            if (vendor) {
                this.local.row = {
                    ...this.local.row,
                    vendorNumber: vendor.vendorNumber,
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vendorNumber: '',
            };
        }
    }

    @Watch('local.row.vehicleContractId')
    async vehicleContractIdChange(vehicleContractId: string | undefined) {
        if (vehicleContractId) {
            const vehicleContract = await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                vehicleContractId,
            );
            if (vehicleContract) {
                this.local.row = {
                    ...this.local.row,
                    vehicleRegistrationNumber: vehicleContract.vehicleRegistrationNumber ?? '',
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vehicleRegistrationNumber: '',
            };
        }
    }

    public async validate(): Promise<boolean> {
        if (!this.local.row.vendorId) {
            return true;
        }
        return await (this.$refs.observer as any).validate();
    }

    public async add(vehicleContractId: string) {
        if (!this.local.row.vendorId) {
            return;
        }

        await postResource<VendorContract>(this.resourceType, {
            ...this.local.row,
            vehicleContractId: vehicleContractId,
        });
    }

    back() {
        this.$router.go(-1);
    }
}
